import { formatOrderMessage } from '@/helpers/orderHelper';
import { useAppSelector } from '@/state/hooks';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { selectActiveTicker } from '@/state/reducers/tickerSlice';
import { LumeFxSide } from '@/types/lumeFx';
import BigNumber from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';
import useActivePair from './useActivePair';
import { UseCurrencyReturn, getCurrency, useCurrency } from './useCurrency';
import { UseInstrumentReturn, useInstrument } from './useInstrument';
import useMinOrderSize from './useMinOrderSize';
import useOrderBook, { UseOrderBookReturn } from './useOrderBook';

type OrderExecutionMode = 'spot' | 'trader' | 'popup';

export interface UseOrderExecutionReturn {
    mode: OrderExecutionMode;
    ccy1: string;
    ccy2: string;
    sideState: [LumeFxSide, React.Dispatch<React.SetStateAction<LumeFxSide>>];
    currencyState: [string, React.Dispatch<React.SetStateAction<string>>];
    priceCurrency: UseCurrencyReturn;
    pairMapToUse: PairMap;
    orderBook: UseOrderBookReturn;
    orderMessage: string;
    instrument: UseInstrumentReturn;
    isCcy2Order: boolean;
    orderDecimals: number;
    minOrderSize: number;
    ccy1Config: UseCurrencyReturn;
    ccy2Config: UseCurrencyReturn;
    activeCurrencyConfig: UseCurrencyReturn;
}

const emptyPairMap: PairMap = {
    show: '',
    celer: '',
    netdania: ''
};

const useOrderExecution = (mode: OrderExecutionMode, pair?: PairMap): UseOrderExecutionReturn => {
    const activePair = useActivePair();
    const activeTicker = useAppSelector(selectActiveTicker);

    const pairMapToUse = useMemo(() => {
        if (mode === 'popup') {
            return pair || emptyPairMap;
        } else if (mode === 'trader') {
            return activeTicker;
        } else if (mode === 'spot') {
            return pair || activePair;
        }
        return emptyPairMap;
    }, [mode, activePair, activeTicker, pair]);

    const [ccy1, ccy2] = useMemo(() => pairMapToUse?.celer?.split('/') || [], [pairMapToUse?.celer]);

    const sideState = useState<LumeFxSide>(LumeFxSide.BUY);
    const currencyState = useState<string>(ccy1);

    const ccy1Config = useCurrency(ccy1);
    const ccy2Config = useCurrency(ccy2);

    const isCcy2Order = useMemo(() => currencyState[0] === ccy2, [currencyState[0], ccy2]);

    const instrument = useInstrument(pairMapToUse?.celer);
    const orderBook = useOrderBook(pairMapToUse?.celer, sideState[0], isCcy2Order);

    const activeCurrencyConfig = useMemo(
        () => (currencyState[0] === ccy1 ? ccy1Config : ccy2Config),
        [currencyState[0]]
    );

    const priceCurrency = useMemo(() => {
        if (['Index', 'Commodity', 'Future'].includes(instrument?.type || '')) {
            return getCurrency(instrument.config.ccy2 || '');
        } else return ccy2Config;
    }, [instrument, ccy2Config]);

    const orderMessage = useMemo(
        () =>
            formatOrderMessage({
                side: sideState[0],
                isCcy2Order,
                ccy1Config,
                ccy2Config,
                pairMapToUse,
                instrumentConfig: instrument
            }),
        [sideState[0], isCcy2Order, ccy1Config, ccy2Config]
    );

    const { orderDecimals, minOrderSize } = useMinOrderSize({ instrument, activeCurrencyConfig });

    useEffect(() => {
        currencyState[1](ccy1);
    }, [pairMapToUse?.celer]);

    return {
        mode,
        ccy1,
        ccy2,
        sideState,
        currencyState,
        priceCurrency,
        orderBook,
        orderMessage,
        instrument,
        activeCurrencyConfig,
        pairMapToUse,
        orderDecimals,
        minOrderSize,
        ccy1Config,
        ccy2Config,
        isCcy2Order
    };
};

export default useOrderExecution;

type GetCurrencyPricesReturn = {
    ccy1Quantity: number;
    ccy2Quantity: number;
};

export function getCurrencyPrices(
    side: LumeFxSide | string,
    instrument: UseInstrumentReturn,
    quantity: number,
    price: number,
    isCcy2Order: boolean
): GetCurrencyPricesReturn {
    if (side !== LumeFxSide.BUY && side !== LumeFxSide.SELL) {
        // Invalid side, return zero quantities
        return {
            ccy1Quantity: 0,
            ccy2Quantity: 0
        };
    }

    // Validate price
    if (!isFinite(price) || price <= 0) {
        // Invalid price, return zero quantities
        return { ccy1Quantity: 0, ccy2Quantity: 0 };
    }

    const bigNumberQuantity = new BigNumber(quantity); // Convert quantity to BigNumber for precision
    const bigNumberPrice = new BigNumber(price); // Convert price to BigNumber for precision

    // Mapping approach to calculate ccy1 and ccy2 signs and multipliers
    // This avoids nested if statements for better readability
    let ccy1Quantity: BigNumber;
    let ccy2Quantity: BigNumber;

    if (!isCcy2Order) {
        // Trading currency 1
        if (side === LumeFxSide.BUY) {
            // BUY CCY1, ccy1Quantity is positive,
            ccy1Quantity = bigNumberQuantity;
            // SELL CCY2, ccy2Quantity is negative and ccy2Quantity is quantity * price
            ccy2Quantity = bigNumberQuantity.multipliedBy(bigNumberPrice).negated();
        } else {
            // SELL CCY1, ccy1Quantity is negative
            ccy1Quantity = bigNumberQuantity.negated();
            // BUY CCY2, ccy2Quantity is positive and ccy2Quantity is quantity * price
            ccy2Quantity = bigNumberQuantity.multipliedBy(bigNumberPrice);
        }
    } else {
        // Trading currency 2
        if (side === LumeFxSide.BUY) {
            // BUY CCY2, SELL CCY1 - ccy1Quantity is negative, ccy2Quantity is positive and ccy1Quantity is quantity / price
            ccy1Quantity = bigNumberQuantity.dividedBy(bigNumberPrice).negated();
            ccy2Quantity = bigNumberQuantity;
        } else {
            // SELL CCY2, BUY CCY1 - ccy1Quantity is positive, ccy2Quantity is negative and ccy1Quantity is quantity / price
            ccy1Quantity = bigNumberQuantity.dividedBy(bigNumberPrice);
            ccy2Quantity = bigNumberQuantity.negated();
        }
    }

    const ccy1Decimals = instrument.quantityDecimals;
    // For cfds, quantity2Decimals may be -1, so we fall back to quantityDecimals
    const ccy2Decimals = instrument.quantityDecimals;

    // Round the quantities to the specified number of decimals for each currency
    ccy1Quantity = ccy1Quantity.decimalPlaces(ccy1Decimals || 0, BigNumber.ROUND_HALF_EVEN);
    ccy2Quantity = ccy2Quantity.decimalPlaces(ccy2Decimals || 0, BigNumber.ROUND_HALF_EVEN);

    return {
        ccy1Quantity: ccy1Quantity.toNumber(),
        ccy2Quantity: ccy2Quantity.toNumber()
    };
}
